import React, { useState, useRef } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ActionButton from "../../shared/ActionButton";
import { PROJECT_COLOR, PROJECT_COLOR_DARK } from "../../../constants/colors";
import { useSkillSheetMainStepper } from "../context/SkillSheetMainStepperContext";
import { buildDemoFormData } from "../utils/demoSignupPayload";
import { DEMO_PDF_PATH } from "../utils/demoConstants";
import { isDemoPdfBodyWithinLimit } from "../utils/demoPdfPayload";
import { trackDemoEvent } from "../utils/demoTracking";

// loadイベントが何らかの理由で発火しなかった場合のフォールバック。
// デプロイ直後などRailsのテンプレートコンパイルが走る初回リクエストは
// 数秒〜十数秒かかることがあるため、通常時より長めに取る
const FALLBACK_REENABLE_DELAY_MS = 20000;

// デモ：「PDFで確認する」。現時点の入力内容をPDF生成APIへ送り、別タブで開く。
// fetch+blobだとPDFビューアの保存時にファイル名が失われるため、
// 実際のフォームPOST（target=_blank）でブラウザに直接レスポンスを渡す
export default function DemoPdfConfirmButton() {
  const { flushDemoDraft, getValues } = useSkillSheetMainStepper();
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // 20秒のフォールバックタイマーは生成が早く終わっても残る古いタイマーが
  // 次の生成中にボタンを有効に戻さないよう、最新の生成だけがボタンを戻せるようにする
  const latestGenerationRef = useRef(0);

  const handleClick = () => {
    if (pdfGenerating) return;

    flushDemoDraft();

    let demoFormData;
    try {
      demoFormData = buildDemoFormData(getValues());
    } catch {
      // mergeDemoInput が defaults のキーと型に一致する値だけでオブジェクトを作り直すため、
      // 入力データ経由ではここに到達しない。将来の変更で到達しうるため握りつぶしだけ残す。
      return;
    }

    trackDemoEvent("demo_pdf_confirm");

    const generation = ++latestGenerationRef.current;
    setPdfGenerating(true);
    submitPdfForm(demoFormData, () => {
      if (latestGenerationRef.current === generation) setPdfGenerating(false);
    });
  };

  return (
    <ActionButton
      label={pdfGenerating ? "生成中..." : "PDFで確認する"}
      icon={<PictureAsPdfIcon sx={{ color: PROJECT_COLOR }} />}
      onClick={handleClick}
      disabled={pdfGenerating}
      sx={{
        backgroundColor: PROJECT_COLOR,
        "&:hover": { backgroundColor: PROJECT_COLOR_DARK },
      }}
    />
  );
}

// 隠しフォームを作ってPOST送信し、別タブでPDFレスポンスをそのまま表示させる。
// window.openで得たウィンドウ参照とフォームのtargetを同じ名前にしておくことで、
// そのウィンドウのloadイベントから生成完了（＝ボタンを再度押せるタイミング）を検知する
function submitPdfForm(demoFormData, onDone) {
  const csrfToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content");

  const targetName = `demo_pdf_${Date.now()}`;
  const pdfWindow = window.open("", targetName);

  const form = document.createElement("form");
  form.method = "POST";
  form.action = DEMO_PDF_PATH;
  form.target = targetName;
  form.style.display = "none";

  form.appendChild(hiddenInput("authenticity_token", csrfToken));
  form.appendChild(
    hiddenInput(
      "demo_form_data",
      isDemoPdfBodyWithinLimit(csrfToken, demoFormData) ? demoFormData : "",
    ),
  );

  document.body.appendChild(form);
  form.submit();
  form.remove();

  // ポップアップブロック等でwindow参照が取れない場合はフォールバックのみに頼る。
  // 両方発火してonDoneが2回呼ばれても、呼び出し側が最新の生成だけを見るので実害はない
  pdfWindow?.addEventListener("load", onDone, { once: true });
  setTimeout(onDone, FALLBACK_REENABLE_DELAY_MS);
}

function hiddenInput(name, value) {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value || "";
  return input;
}
